import React, { useState, useEffect } from 'react';
import api from '../api';

const COUNTRIES = [
  { code: 'ES', label: '🇪🇸 Espagne' },
  { code: 'PL', label: '🇵🇱 Pologne' },
  { code: 'FR', label: '🇫🇷 France' },
  { code: 'BE', label: '🇧🇪 Belgique' },
  { code: 'IT', label: '🇮🇹 Italie' },
  { code: 'DE', label: '🇩🇪 Allemagne' },
  { code: 'PT', label: '🇵🇹 Portugal' },
  { code: 'NL', label: '🇳🇱 Pays-Bas' },
  { code: 'LU', label: '🇱🇺 Luxembourg' },
  { code: 'GB', label: '🇬🇧 Royaume-Uni' },
  { code: 'US', label: '🇺🇸 États-Unis' },
  { code: 'CH', label: '🇨🇭 Suisse' },
  { code: 'AT', label: '🇦🇹 Autriche' },
  { code: 'CZ', label: '🇨🇿 Tchéquie' },
  { code: 'SK', label: '🇸🇰 Slovaquie' },
  { code: 'RO', label: '🇷🇴 Roumanie' },
  { code: 'IE', label: '🇮🇪 Irlande' },
  { code: 'SE', label: '🇸🇪 Suède' },
  { code: 'DK', label: '🇩🇰 Danemark' },
  { code: 'OTHER', label: '🌍 Autre' },
];

// Nom "annuaire" : NOM Prénom pour un particulier, sinon la raison sociale
function computeName(form) {
  if (form.type === 'particulier') {
    return `${(form.last_name || '').trim()} ${(form.first_name || '').trim()}`.trim();
  }
  return (form.name || '').trim();
}

function emptyForm() {
  return { name: '', first_name: '', last_name: '', client_number: '', type: 'particulier', country: 'ES', vat_number: '', email: '', phone: '', address: '', notes: '', hourly_rate: '' };
}

function fmtMoney(n) {
  return Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [search, setSearch] = useState('');

  const load = async () => {
    try {
      const r = await api.get('/clients');
      setClients(r.data);
    } catch {}
  };

  useEffect(() => { load(); }, []);

  const openCreate = async () => {
    setEditingId(null);
    setForm(emptyForm());
    setShowModal(true);
    // Pré-remplir le prochain numéro automatique (modifiable, à effacer pour un ancien client)
    try {
      const r = await api.get('/clients/next-number');
      if (r.data && r.data.number) setForm(f => ({ ...f, client_number: r.data.number }));
    } catch { /* ignore */ }
  };

  const openEdit = (client) => {
    setEditingId(client.id);
    setForm({
      name: client.name,
      first_name: client.first_name || '',
      last_name: client.last_name || '',
      client_number: client.client_number || '',
      type: client.type,
      country: client.country || 'ES',
      vat_number: client.vat_number || '',
      email: client.email || '',
      phone: client.phone || '',
      address: client.address || '',
      notes: client.notes || '',
      hourly_rate: client.hourly_rate || '',
    });
    setShowModal(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const finalName = computeName(form);
    if (!finalName) {
      alert(form.type === 'particulier' ? 'Le nom de famille est requis.' : 'La raison sociale est requise.');
      return;
    }
    // Validation : société hors Espagne doit avoir un numéro de TVA intracom
    if (form.type === 'societe' && form.country !== 'ES' && !form.vat_number.trim()) {
      if (!window.confirm('Une société hors Espagne devrait avoir un numéro de TVA intracommunautaire pour justifier le 0%. Enregistrer quand même ?')) {
        return;
      }
    }
    const payload = {
      ...form,
      name: finalName,
      first_name: form.type === 'particulier' ? (form.first_name || null) : null,
      last_name: form.type === 'particulier' ? (form.last_name || null) : null,
      client_number: form.client_number || null,
      vat_number: form.vat_number || null,
      email: form.email || null,
      phone: form.phone || null,
      address: form.address || null,
      notes: form.notes || null,
      hourly_rate: form.hourly_rate || null,
    };
    try {
      if (editingId) await api.put(`/clients/${editingId}`, payload);
      else await api.post('/clients', payload);
      setShowModal(false);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur');
    }
  };

  const deleteClient = async (id) => {
    if (!window.confirm('Supprimer ce client ? Les factures liées ne seront pas supprimées mais perdront le lien.')) return;
    await api.delete(`/clients/${id}`);
    load();
  };

  const filtered = search
    ? clients.filter(c => (c.name + (c.email || '') + (c.vat_number || '')).toLowerCase().includes(search.toLowerCase()))
    : clients;

  return (
    <div>
      <div className="page-header page-header-row">
        <div>
          <h1>Clients</h1>
          <p>{clients.length} client{clients.length !== 1 ? 's' : ''}</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>+ Nouveau client</button>
      </div>

      <div style={{ marginBottom: 16 }}>
        <input className="form-input" placeholder="🔍 Rechercher..." value={search} onChange={e => setSearch(e.target.value)} style={{ maxWidth: 360 }} />
      </div>

      {filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-emoji">👥</div>
          <p>{clients.length === 0 ? 'Aucun client. Créez votre premier client !' : 'Aucun résultat.'}</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
          {filtered.map(client => (
            <div key={client.id} className="card" style={{ cursor: 'pointer', padding: 16 }} onClick={() => openEdit(client)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: 2 }}>
                    {client.type === 'societe' ? '🏢' : '👤'} {client.name}
                    {client.client_number && <span style={{ marginLeft: 6, fontSize: '0.72rem', fontWeight: 700, color: 'var(--primary)', background: 'var(--primary-bg)', padding: '1px 6px', borderRadius: 6 }}>N° {client.client_number}</span>}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-light)' }}>
                    {COUNTRIES.find(c => c.code === client.country)?.label || client.country}
                    {client.vat_number && ' · ' + client.vat_number}
                  </div>
                  {client.email && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>✉️ {client.email}</div>}
                </div>
                <button className="btn-ghost" onClick={e => { e.stopPropagation(); deleteClient(client.id); }}>🗑️</button>
              </div>
              <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--border-light)', display: 'flex', gap: 10, fontSize: '0.75rem' }}>
                <div>
                  <div style={{ color: 'var(--text-muted)', fontWeight: 600 }}>Facturé</div>
                  <div style={{ fontWeight: 700 }}>{fmtMoney(client.total_facture)}</div>
                </div>
                <div>
                  <div style={{ color: 'var(--text-muted)', fontWeight: 600 }}>Encaissé</div>
                  <div style={{ fontWeight: 700, color: 'var(--success)' }}>{fmtMoney(client.total_paye)}</div>
                </div>
                <div>
                  <div style={{ color: 'var(--text-muted)', fontWeight: 600 }}>Reste</div>
                  <div style={{ fontWeight: 700, color: (client.total_facture - client.total_paye) > 0 ? 'var(--warning)' : 'var(--text-muted)' }}>
                    {fmtMoney(client.total_facture - client.total_paye)}
                  </div>
                </div>
                <div style={{ marginLeft: 'auto', alignSelf: 'center', color: 'var(--text-muted)' }}>{client.nb_factures} fact.</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <h2 style={{ margin: 0 }}>{editingId ? 'Modifier le client' : 'Nouveau client'}</h2>
              <button type="button" className="btn-icon" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <form onSubmit={save}>
              <div className="form-row">
                <div className="form-group">
                  <label>Type *</label>
                  <select className="form-input" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                    <option value="particulier">👤 Particulier</option>
                    <option value="societe">🏢 Société</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Pays *</label>
                  <select className="form-input" value={form.country} onChange={e => setForm({ ...form, country: e.target.value })}>
                    {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>N° client</label>
                  <input className="form-input" value={form.client_number} onChange={e => setForm({ ...form, client_number: e.target.value })} placeholder="Ex : 2600535" />
                </div>
              </div>
              {!editingId && (
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: -8, marginBottom: 10 }}>
                  💡 Le N° client est <strong>pré-rempli automatiquement</strong>. Pour un <strong>ancien</strong> client (déjà dans B4T), efface-le et colle son numéro B4T.
                </div>
              )}

              {form.type === 'particulier' ? (
                <div className="form-row">
                  <div className="form-group">
                    <label>Nom (de famille) *</label>
                    <input className="form-input" value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} placeholder="Ex : Kazimierski" autoFocus />
                  </div>
                  <div className="form-group">
                    <label>Prénom</label>
                    <input className="form-input" value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} placeholder="Ex : Cyprian" />
                  </div>
                </div>
              ) : (
                <div className="form-group">
                  <label>Raison sociale *</label>
                  <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Ex : Ligural Sp. z o.o." autoFocus />
                </div>
              )}

              {form.type === 'societe' && form.country !== 'ES' && (
                <div style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning)', padding: 10, borderRadius: 8, marginBottom: 14, fontSize: '0.82rem' }}>
                  ⚠️ Société hors Espagne → <strong>TVA 0 % intracommunautaire</strong> appliquée automatiquement aux factures. Le N° de TVA intracom est indispensable pour justifier.
                </div>
              )}

              <div className="form-group">
                <label>Numéro de TVA intracommunautaire {form.type === 'societe' && form.country !== 'ES' && '*'}</label>
                <input className="form-input" value={form.vat_number} onChange={e => setForm({ ...form, vat_number: e.target.value })} placeholder={form.country === 'ES' ? 'ESB12345678' : form.country === 'FR' ? 'FR12345678901' : 'Numéro TVA intracom'} />
              </div>

              <div className="form-group">
                <label>Taux horaire par défaut (€ / h)</label>
                <input className="form-input" type="number" step="0.01" value={form.hourly_rate} onChange={e => setForm({ ...form, hourly_rate: e.target.value })} placeholder="Ex : 150 — pré-rempli à la saisie des prestations" />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Email</label>
                  <input className="form-input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Téléphone</label>
                  <input className="form-input" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
                </div>
              </div>

              <div className="form-group">
                <label>Adresse</label>
                <textarea className="form-input" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} rows={2} style={{ minHeight: 60 }} />
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea className="form-input" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} style={{ minHeight: 60 }} />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary">{editingId ? 'Enregistrer' : 'Créer'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
